import { memo, useCallback } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { Tooltip } from '@flipster/react/design-system-legacy/tooltip';
import Formatted from '@flipster/react/design-utils/formatted';
import { css, sva } from '@flipster/styled-system/css';
import { grid } from '@flipster/styled-system/patterns';
import Num, { BigNumber, formatToShortNumber } from '@flipster/utils/num';

import type { OrderbookDisplayMode } from '@web/app/[locale]/(web)/trade/_misc/state';

export const rowHeight = 23;

export interface OrderbookItem {
  price: BigNumber | null;
  size: BigNumber | null;
  sum: BigNumber | null;
  sumQty: BigNumber | null;
  avgPrice: BigNumber | null;
  qty: BigNumber | null;
}
interface OrderbookRowProps {
  currency: string;
  quoteCurrency: string;
  precision: number;
  qtyPrecision: number;
  type: 'ask' | 'bid';
  percentage: number;
  isHovered?: boolean;
  isActiveHover?: boolean;
  index: number;
  onMouseEnter?: (index: number) => void;
  onClick?: (index: number) => void;
  item: OrderbookItem;
  isMarked: boolean;
  displayMode: OrderbookDisplayMode;
}

const orderbookRowRecipe = sva({
  slots: ['container', 'volumeBar', 'priceText', 'dot'],
  base: {
    container: {
      position: 'relative',
      h: rowHeight,
      pb: 2,
      cursor: 'pointer',
      borderBottomWidth: '1px',
      borderBottomStyle: 'solid',
      borderTopWidth: '1px',
      borderTopStyle: 'solid',
      borderColor: 'transparent',
    },
    volumeBar: {
      position: 'absolute',
      right: 0,
      top: 0,
      bottom: 0,
      transition: 'width 0.3s ease',
      rounded: 4,
    },
    priceText: {
      textStyle: 'Text/S/Monospace/Strong',
    },
    dot: {
      w: 4,
      h: 4,
      rounded: 999,
      bg: 'background.accent.primary',
      pos: 'absolute',
      left: -8,
      transform: 'translateX(-50%)',
    },
  },
  variants: {
    type: {
      ask: {
        volumeBar: {
          bg: 'background.state.negative.tint',
        },
        priceText: {
          color: 'content.state.negative',
        },
      },
      bid: {
        volumeBar: {
          bg: 'background.state.positive.tint',
        },
        priceText: {
          color: 'content.accent.buy',
        },
      },
    },
    isHovered: {
      true: {
        container: {
          bg: 'background.interaction.hover',
        },
      },
      false: {
        container: {
          bg: 'transparent',
        },
      },
    },
    isEmpty: {
      true: {
        container: {
          cursor: 'default',
        },
      },
    },
    isActiveHover: {
      true: {},
      false: {},
    },
  },
  compoundVariants: [
    {
      isActiveHover: true,
      type: 'ask',
      css: {
        container: {
          borderTopColor: 'border.default.level-3',
        },
      },
    },
    {
      isActiveHover: true,
      type: 'bid',
      css: {
        container: {
          borderBottomColor: 'border.default.level-3',
        },
      },
    },
  ],
});

export const OrderbookRow = memo(function OrderbookRow({
  currency,
  quoteCurrency,
  precision,
  qtyPrecision: _qtyPrecision,
  type,
  percentage,
  isHovered,
  isActiveHover,
  index,
  onMouseEnter,
  onClick,
  item,
  isMarked,
  displayMode,
}: OrderbookRowProps) {
  const { price, size, sum, sumQty, avgPrice, qty } = item;
  const isEmpty = !price && !size && !sum;
  const classes = orderbookRowRecipe({ type, isHovered, isEmpty, isActiveHover });

  const handleMouseEnter = useCallback(() => {
    onMouseEnter?.(index);
  }, [onMouseEnter, index]);

  const handleClick = useCallback(() => {
    onClick?.(index);
  }, [onClick, index]);

  const formatQtyValue = (value: BigNumber) => {
    if (value.isZero()) return '0';
    // 0 < |value| < 0.001 — round-floor로 0이 되는 정보 손실 대신 미만 표기
    if (value.abs().lt(0.001)) return '< 0.001';

    // 소수점 3자리 내림 + 후행 0 제거 + 대량 축약 (K, M)
    const truncated = Num(value).decimalPlaces(3, BigNumber.ROUND_FLOOR);
    return formatToShortNumber(truncated.toString(), { withComma: false, precision: 3 });
  };

  const formatValue = (value: BigNumber | null) => {
    if (!value) return '0';
    switch (displayMode) {
      case 'qty': {
        return formatQtyValue(value);
      }
      case 'amount':
      default: {
        return formatToShortNumber(value.toString(), { withComma: false });
      }
    }
  };

  const rowContent = (
    <div
      className={classes.container}
      onMouseEnter={isEmpty ? undefined : handleMouseEnter}
      onClick={isEmpty ? undefined : handleClick}
    >
      <div
        className={grid({
          columns: 3,
          h: 21,
          alignItems: 'center',
          position: 'relative',
          zIndex: 1,
        })}
      >
        {!isEmpty && <div className={classes.volumeBar} style={{ width: `${percentage * 100}%` }} />}
        {isEmpty ? (
          <div />
        ) : (
          <>
            {isMarked && <div className={classes.dot} />}
            <div className={classes.priceText}>
              <Formatted.Number value={price} precision={precision} toFixed />
            </div>
            <div
              className={css({
                textStyle: 'Text/S/Monospace/Regular',
                color: 'content.default.level-5',
                textAlign: 'right',
              })}
            >
              {formatValue(displayMode === 'amount' ? size : qty)}
            </div>
            <div
              className={css({
                textStyle: 'Text/S/Monospace/Regular',
                color: 'content.default.level-5',
                textAlign: 'right',
              })}
            >
              {formatValue(displayMode === 'amount' ? sum : sumQty)}
            </div>
          </>
        )}
      </div>
    </div>
  );

  // Tooltip(floating-ui) 기계를 40개 row마다 상주시키지 않고, hover 중인 row 하나에만 마운트한다
  if (isEmpty || !isActiveHover) return rowContent;

  return (
    <Tooltip placement='left' shape='gray' open enableFlip>
      <Tooltip.Trigger>{rowContent}</Tooltip.Trigger>
      <Tooltip.Content portal>
        <dl
          className={css({
            '& > div': {
              display: 'flex',
              gap: 4,
            },
            '& dt': {
              textStyle: 'Text/S/Regular',
              color: 'content.default.level-2',
            },
            '& dd': {
              textStyle: 'Text/S/Monospace/Regular',
              color: 'content.default.level-5',
            },
          })}
        >
          <div>
            <dt>
              <FormattedMessage id='Autogen_common.r251126.s_Avg_Price' />
            </dt>
            <dd>
              <Formatted.Number value={avgPrice} precision={precision} method='round-half-up' toFixed />
            </dd>
          </div>

          <div>
            <dt>
              <FormattedMessage
                id='Autogen_common.r251126.s_Sum_currency'
                values={{
                  currency: currency,
                }}
              />
            </dt>
            <dd>{formatQtyValue(sumQty || Num(0))}</dd>
          </div>

          <div>
            <dt>
              <FormattedMessage
                id='Autogen_common.r251126.s_Sum_quoteCurrency'
                values={{
                  quoteCurrency: quoteCurrency,
                }}
              />
            </dt>
            <dd>{formatToShortNumber(sum?.toString() || '0', { withComma: false })}</dd>
          </div>
        </dl>
      </Tooltip.Content>
    </Tooltip>
  );
});
